import React, {useState, useEffect} from "react";
import {inject, observer} from "mobx-react";
import {useLocation} from "react-router-dom";
import {Form, Formik} from "formik";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import debounce from "lodash.debounce";
//local
import {toProjectId, PROJECTS} from "constants/routes.const";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {OffersSwitchOffModal} from "../RequestEdit/OffersSwitchOffModal";
import {ChooseCategoriesModal} from "../RequestEdit/ChooseCategoriesModal";
import {RemindersModal} from "../RequestEdit/RemindersModal";
import EditDatePicker from "components/EditDatePicker";
import EditSelect from "components/selects/EditSelect";
import MiniSelect from "components/selects/MiniSelect";
import {Tag} from "components/Tag";
import SimpleField from "components/SimpleField";
import FormikField from "components/FormikField";
import {Info} from "components/Info";
import {IconChevronLeft, IconPlus, IconCurrency, IconAgreement} from "components/icons";
import {ProjectLabel} from "components/ProjectLabel";
import {isObject, getUserDropdownList} from "helpers/helper";
import {color} from "constants/color.consts";
import {STANDARD_AGREEMENT_TYPES, rfpStatuses} from "constants/buyer.consts";
import {requestSchema} from "./formAttrs";
import cn from "classnames";

const RequestHeadEdit = inject("store")(
  observer(({store: {buyer, clientStore, auth, nsi}, settings = {}}) => {
    const {currentRequest, isEdit, rfpRecipientsFilterParams} = buyer;
    const {language} = clientStore;
    const {user, activeOrganization} = auth;
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const [isChooseCategoriesModal, setChooseCategoriesModal] = useState(false);
    const [isHideOffersSwitchOffModal, setHideOffersSwitchOffModal] = useState(false);
    const [isHardDateCloseOffersSwitchOffModal, setHardDateCloseOffersSwitchOffModal] =
      useState(false);
    const [isRemindersModal, setRemindersModal] = useState(false);
    const [currencies, setCurrencies] = useState([]);
    const [agreements, setAgreements] = useState([]);
    const isAgreement =
      currentRequest.status === rfpStatuses.DRAFT &&
      (!currentRequest.standardAgreement ||
        currentRequest.standardAgreement ===
          STANDARD_AGREEMENT_TYPES.NO_STANDARD_AGREEMENT);

    const requestLocation = currentRequest.location ?? currentRequest.Project?.location;
    const projectCoordinates = {
      lat: +currentRequest.Project?.locationLat,
      lng: +currentRequest.Project?.locationLng,
    };
    const requestCoordinates = {
      lat: +currentRequest.locationLat,
      lng: +currentRequest.locationLng,
    };
    const initialLocation = currentRequest.location
      ? requestCoordinates
      : projectCoordinates;
    const noLocation = Object.values(initialLocation).some((item) => !item);

    const membersList = location?.state?.project?.id
      ? location?.state?.project?.Members
      : getUserDropdownList(currentRequest?.Project?.Members);

    const CategoriesButton = ({categories}) => (
      <ButtonTransparent
        name={
          categories.length > 1
            ? `${t(categories[0])} +${categories.length - 1}`
            : categories.length
            ? categories[0]
            : "CREATE_RFP_CATEGORIES_BUTTON"
        }
        onClick={() => setChooseCategoriesModal(true)}
        icon={!categories.length && <IconPlus />}
        nonTransparent={categories.length}
        rounded
        small
      />
    );

    const changeRequest = debounce((data) => buyer.updateCurrentRequestInfo(data), 1000);
    const changeRequestNow = (data) => buyer.updateCurrentRequestInfo(data);

    const handleChange = (e) => {
      buyer.setDraftSavedStatus(false);
      if (e?.target && e.target.name === "name") changeRequest({name: e.target.value});

      if (e?.target && e.target.name === "location")
        changeRequestNow({location: e.target.value});
      if (e?.coordinates)
        changeRequestNow({
          location: e.coordinates.location,
          locationLat: e.coordinates.lat,
          locationLng: e.coordinates.lng,
        });
      if (e?._d && +new Date(e) > Date.now())
        changeRequestNow({
          closedAt: new Date(new Date(e._d).setHours(23, 59, 59, 0)).toISOString(),
        });
      if (isObject(e) && "offersHidden" in e) changeRequestNow(e);
      if (isObject(e) && "hardDateCloseOffers" in e) changeRequestNow(e);
      if (isObject(e) && "CurrencyCode" in e) changeRequestNow(e);
      if (isObject(e) && "standardAgreement" in e) changeRequestNow(e);
      if (isObject(e) && "reminders" in e) changeRequestNow(e);
      if (isObject(e) && "reminderDays" in e) changeRequestNow(e);
      if (isObject(e) && "categories" in e) {
        buyer.setRfpRecipientsFilterParams({
          ...rfpRecipientsFilterParams,
          ...e.categories,
        });
        changeRequestNow(e.categories);
      }
      if (typeof e === "string") changeRequestNow({ContactPersonId: e});
      if (!e) changeRequestNow({closedAt: null});
    };

    useEffect(() => {
      nsi.getCurrencies().then(setCurrencies);
    }, [nsi]);

    useEffect(() => {
      buyer
        .getAgreements()
        .then((response) =>
          setAgreements([
            STANDARD_AGREEMENT_TYPES.NO_STANDARD_AGREEMENT,
            ...(response.data ?? []),
            STANDARD_AGREEMENT_TYPES.OTHER_AGREEMENT,
          ])
        );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      buyer.validateCurrentRequest(
        !!currentRequest.name &&
          !!currentRequest.closedAt &&
          []
            .concat(
              currentRequest.entrepreneurWithin,
              currentRequest.entrepreneurOffers,
              currentRequest.materialSupplierType,
              currentRequest.consultingServices
            )
            .filter((item) => !!item).length
      );
    }, [currentRequest, buyer]);

    useEffect(() => {
      isEdit &&
        buyer.setNewPublishedRFPRecipients({
          notRemoveRegistered: [...currentRequest.recipients.registered],
          notRemoveNotRegistered: [...currentRequest.recipients.notRegistered],
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit]);

    return (
      <div className="request-edit-block">
        {(location?.state?.project?.id ? currentRequest.Project?.id : true) && (
          <Formik
            enableReinitialize
            initialValues={currentRequest}
            validationSchema={requestSchema}
          >
            {({values, setFieldValue}) => (
              <Form onChange={handleChange}>
                <div>
                  <div className="main-fields_wrapper">
                    <div className="main-fields_wrapper__row-info">
                      <div className="main-fields_wrapper__project">
                        {currentRequest.Project?.id && (
                          <ButtonTransparent
                            icon={<IconChevronLeft color={color.purple} />}
                            noBorder
                            small
                            addClass="view-header__back-button"
                            onClick={() =>
                              navigate(
                                currentRequest.Project.id
                                  ? toProjectId(currentRequest.Project.id)
                                  : PROJECTS
                              )
                            }
                          />
                        )}
                        <ProjectLabel
                          name={currentRequest.Project?.name}
                          code={currentRequest.Project?.reference}
                        />
                      </div>
                      <div className="main-fields_wrapper__input name">
                        {activeOrganization?.allowProcurementAccount && (
                          <Tag
                            text={currentRequest?.Project?.ProjectItem?.account}
                            color="black"
                            small
                            noBackground
                            addClass="ml-2 mr-0"
                          />
                        )}
                        <SimpleField
                          name="name"
                          autoFocus
                          placeholder={t("NAME_REQUEST_PLACEHOLDER")}
                          className="input pl-0"
                          highlightIfEmpty={!values.name}
                          addClass="ml-2"
                        />
                        <div
                          className={cn("categories-button-wrapper", {
                            "bg-yellow": ![]
                              .concat(
                                values.entrepreneurOffers,
                                values.entrepreneurWithin,
                                values.materialSupplierType,
                                values.consultingServices
                              )
                              .filter((item) => !!item).length,
                          })}
                        >
                          <CategoriesButton
                            categories={[]
                              .concat(
                                values.entrepreneurOffers,
                                values.entrepreneurWithin,
                                values.materialSupplierType,
                                values.consultingServices
                              )
                              .filter((item) => !!item)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="responsive-lower-fields main-fields_wrapper__row-info">
                      <div className="responsive-left">
                        <div className="main-fields_wrapper__input location">
                          <FormikField
                            name="location"
                            placeholder={t("ADDRESS_PLACEHOLDER")}
                            errorName="location"
                            value={requestLocation}
                            withGoogleMapsAutocomplete
                            withGoogleMapsModal
                            language={language}
                            initialLocation={noLocation ? null : {...initialLocation}}
                            onChangeCoordinates={({lat, lng, location}) => {
                              setFieldValue("location", location);
                              setFieldValue("locationLat", lat);
                              setFieldValue("locationLng", lng);
                              handleChange({coordinates: {lat, lng, location}});
                            }}
                            className="input mb-0"
                          />
                        </div>
                        <div className="close-date">
                          <EditDatePicker
                            name="closedAt"
                            placeholder="CLOSES_FOR_OFFERS"
                            highlightIfEmpty={!values.closedAt}
                            onChange={handleChange}
                          />
                          <Info tooltip="RFP_CLOSE_DATE_INFO_TOOLTIP" />
                        </div>
                        <div className="toggles">
                          <div className="df-row-center">
                            <IconAgreement addClass="mr-1" />
                            <MiniSelect
                              list={agreements}
                              highlightIfEmpty={isAgreement}
                              colorValues={{
                                [STANDARD_AGREEMENT_TYPES.NO_STANDARD_AGREEMENT]:
                                  color.midGray,
                              }}
                              defaultValue={
                                currentRequest.standardAgreement
                                  ? currentRequest.standardAgreement
                                  : STANDARD_AGREEMENT_TYPES.NO_STANDARD_AGREEMENT
                              }
                              fullWidthDropdownLabel
                              onChange={(value) => {
                                setFieldValue("standardAgreement", value);
                                handleChange({
                                  standardAgreement: value,
                                });
                              }}
                              informationText="RFP_STANDARD_AGREEMENT_TOOLTIP"
                              addDropdownClass="wdth-170"
                            />
                          </div>
                          <div className="df-row-center">
                            <IconCurrency addClass="mr-1" />
                            <MiniSelect
                              list={currencies ?? []}
                              defaultValue={
                                currentRequest.CurrencyCode ??
                                user.ActiveOrganization?.defaultCurrencyCode
                              }
                              onChange={(value) => {
                                setFieldValue("CurrencyCode", value);
                                handleChange({CurrencyCode: value});
                              }}
                              informationText="RFP_CURRENCY_TOOLTIP"
                            />
                          </div>
                        </div>
                      </div>
                      <EditSelect
                        addClass="contact-person"
                        name="ContactPersonId"
                        list={membersList}
                        onChange={handleChange}
                        defaultValue={membersList[0]?.value}
                        initials
                        ownValue={user.id}
                        label={`${t("OUR_CONTACT")}:`}
                      />
                    </div>
                  </div>
                </div>
                {isHardDateCloseOffersSwitchOffModal && (
                  <OffersSwitchOffModal
                    title="MODAL_HARD_DATE_SWITCH_TITLE"
                    description="MODAL_HARD_DATE_CLOSE_OFFERS_SWITCH_DESCRIPTION"
                    onOk={() => {
                      setFieldValue("hardDateCloseOffers", false);
                      handleChange({hardDateCloseOffers: false});
                    }}
                    onClose={() => setHardDateCloseOffersSwitchOffModal(false)}
                  />
                )}
                {isHideOffersSwitchOffModal && (
                  <OffersSwitchOffModal
                    title="MODAL_HIDE_OFFERS_SWITCH_TITLE"
                    description="MODAL_HIDE_OFFERS_SWITCH_DESCRIPTION"
                    needAttention
                    onOk={() => {
                      setFieldValue("offersHidden", false);
                      handleChange({offersHidden: false});
                    }}
                    onClose={() => setHideOffersSwitchOffModal(false)}
                  />
                )}
                {isChooseCategoriesModal && (
                  <ChooseCategoriesModal
                    initialCategories={{
                      entrepreneurOffers: values.entrepreneurOffers ?? [],
                      entrepreneurWithin: values.entrepreneurWithin ?? [],
                      materialSupplierType: values.materialSupplierType ?? [],
                      consultingServices: values.consultingServices ?? [],
                    }}
                    onOk={(data) => {
                      Object.entries(data).forEach((category) =>
                        setFieldValue(category[0], category[1])
                      );
                      handleChange({categories: data});
                    }}
                    onClose={() => setChooseCategoriesModal(false)}
                  />
                )}
                {isRemindersModal && (
                  <RemindersModal
                    reminderDays={values.reminderDays ?? []}
                    closedAt={values?.closedAt}
                    onOk={(days) => {
                      setFieldValue("reminderDays", days);
                      handleChange({reminderDays: days});
                    }}
                    onClose={() => setRemindersModal(false)}
                  />
                )}
              </Form>
            )}
          </Formik>
        )}
      </div>
    );
  })
);

export default RequestHeadEdit;
